/**
 * Created by Ning Jiang on 2021-06-16.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {TombstoneRip} from "../../../TombstoneRip";
import {TimelineLite} from "gsap";
import {IBetWayWinData} from "../../../server/data/ITombstoneRipData";
import {BetWaysInitialWinView} from "./BetWaysInitialWinView";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TombstoneRipGameConfig} from "../../../TombstoneRipGameConfig";
import {TombstoneRipSound} from "../../../sound/TombstoneRipSound";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {TombstoneRipSymbolState} from "../../../symbol/TombstoneRipSymbolConfig";

export class BetWaysInitialWinController extends WinPresentationController {

    private _view:BetWaysInitialWinView;
    private _mainTimeline:TimelineLite | null;

    private _belowStakeWinRestriction:boolean;

    constructor() {
        super("BetWaysInitialWinController", true);
        this._view = new BetWaysInitialWinView();
    }

    protected hasWin():boolean {
        TombstoneRip.multiplier.verifyTotalMultiplier();
        return TombstoneRip.model.parsedGameData.betWins.length > 0 && TombstoneRip.model.parsedGameData.singleWin > 0;
    }

    protected async createWinAnimation():Promise<any> {
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        TombstoneRip.playFSWin = true;
        const tl = new TimelineLite();
        tl.add([
            this.getWinAnimation(),
            this.getWinCountUpAnimation()
        ]);
        this._mainTimeline = tl;
        await Promise.resolve(tl);
        this._mainTimeline = null;
    }

    protected getWinAnimation():TimelineLite {
        const winRatio = TombstoneRip.model.parsedGameData.currentWinRatioConfig?.ratio!;
        let moreBetWinSoundPlaying = false;
        const tl = new TimelineLite();
        if(!this._belowStakeWinRestriction) {
            tl.add(() => {TombstoneRipSound.soundPlayer.playTrack({name : this.getSoundName()})});
            if(winRatio >= TombstoneRipGameConfig.WIN_RATIOS[3].ratio && TombstoneRip.model.parsedGameData.betWins.length > 1) {
                moreBetWinSoundPlaying = true;
                tl.add(() => {
                    TombstoneRipSound.soundPlayer.playTrack({
                        name : TombstoneRipSound.INITIAL_MORE_BETWINS,
                        volume : 1
                    });
                    TombstoneRipSound.fadeMusic(0.5, 200);
                });
            }
        }
        let winDelay = 0;
        let winWaysWon = 0;
        TombstoneRip.model.parsedGameData.betWins.forEach(w => {
            winWaysWon += (<IBetWayWinData>w).numberOfCombinations;
            tl.add(this.getSingleBetWinAnimation(<IBetWayWinData>w, winRatio, winWaysWon), winDelay);
            winDelay = (winRatio < TombstoneRipGameConfig.WIN_RATIOS[3].ratio ? 0 : tl.totalDuration() - 0.6);
        });
        tl.add(() => {
            if(moreBetWinSoundPlaying) {
                TombstoneRipSound.fadeAndStopSound({
                    name : TombstoneRipSound.INITIAL_MORE_BETWINS,
                    to : 0,
                    duration : 200
                });
                if(TombstoneRip.model.parsedGameData.currentWinRatioConfig!.bigWinLevel === -1) {
                    TombstoneRipSound.fadeMusic(1, 200);
                }
            }
        });
        return tl;
    }

    protected getSingleBetWinAnimation(winData:IBetWayWinData, winRatio:number, winWaysWon:number):TimelineLite {
        const tl = new TimelineLite();
        if(!this._belowStakeWinRestriction) {
            let symbolDelay = 0;
            const reelDelay = this.getReelDelay(winRatio);
            const winTextPosition = this.getWinTextPosition(winData.reelWinPositions);
            winData.reelWinPositions.forEach((reelWin, reelId) => {
                let hasReelWin = false;
                reelWin.forEach((winPosition, symId) => {
                    if(winPosition) {
                        hasReelWin = true;
                        tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, reelId === winTextPosition.x && symId === winTextPosition.y, winData), symbolDelay);
                    }
                });
                if(hasReelWin) {
                    symbolDelay += reelDelay;
                }
            });

            // add WinWaysWon animation.
            tl.add(TombstoneRip.waysWon.showWinWaysWon(winWaysWon, TombstoneRip.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1), 0);
        } else {
            tl.add(() => {}, 0.1);
        }
        return tl;
    }

    private getWinTextPosition(reelWinPositions:boolean[][]):PIXI.Point {
        for(let reelId = reelWinPositions.length - 1; reelId >= 0; reelId--) {
            if(TombstoneRip.model.parsedGameData.doReelNudge[reelId]) {
                continue;
            }
            for(let symId = 0; symId < 3; symId++) {
                if(reelWinPositions[reelId][symId] && !TombstoneRip.model.parsedGameData.reels[reelId][symId + 1].includes("W") && !TombstoneRip.model.parsedGameData.reels[reelId][symId + 1].includes("S")) {
                    return new PIXI.Point(reelId, symId);
                }
            }
        }
        return new PIXI.Point(0, reelWinPositions[0].indexOf(true));
    }

    protected getSymbolWinAnimation(reelId:number, symId:number, winRatio:number, showWinText:boolean, winData:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();

        const sym = TombstoneRip.reels[reelId].getSymbol(symId, false, false);
        tl.add([
            () => TombstoneRip.reels[reelId].reelView.addWinSymbol(sym),
            sym.getStateAnimation(
                TombstoneRipSymbolState.NORMAL,
                {duration : 0.1, alpha : 1},
                {duration : 0.1, alpha : 0}
            )
        ]);// no need to have win state in this game?

        const position = sym.getStagePosition();
        tl.add(this._view.playAnimation(position, this.getEffectLevel(winRatio), reelId === 4, showWinText, winData), 0);

        return tl;
    }

    private getReelDelay(winRatio:number):number {
        // tiny/small
        if(winRatio < TombstoneRipGameConfig.WIN_RATIOS[2].ratio) {
            return 0;
        }

        //mid
        if(winRatio < TombstoneRipGameConfig.WIN_RATIOS[3].ratio) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }

    private getEffectLevel(winRatio:number):number {
        for(let i = 1; i < 4; i++) {
            if(winRatio < TombstoneRipGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    protected getWinCountUpAnimation():TimelineLite {
        const data = TombstoneRip.model.parsedGameData;
        const tl = new TimelineLite();
        if(data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        const duration:number = data.currentWinRatioConfig!.duration;

        // #373
        if(!this._belowStakeWinRestriction) {
            tl.add(TombstoneRip.initialCountUp.getCountUpAnimation(0, data.singleWin, duration, data.currentMultiplier, this._belowStakeWinRestriction));
        }

        tl.add(() => SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, BelowStakeWinRestriction.isWinBelowStake()));

        return tl;
    }

    private getSoundName():string {
        let name = TombstoneRip.model.parsedGameData.currentWinRatioConfig!.name!;
        if(name.includes("BIG")) {
            name = "LARGE";
        }

        return (<any>TombstoneRipSound)[`INITIAL_${name}`];
    }

    protected skip() {
        TombstoneRipSound.soundPlayer.stopTrack(this.getSoundName());
        TombstoneRip.initialCountUp.skip();
        this._mainTimeline?.progress(1);
    }
}